import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { EMPTY_OBJECT } from '../../../constants/common'
import { NUMBER_SIZE } from '../../../constants/size'
import { ButtonSelect } from '../../Select'
import { COLOR_FORMAT_HEX, COLOR_FORMATS } from '../ColorPicker.constants'

import {
  getCommitHex,
  hexToHsv,
  hsvToHex,
  normalizeColorMap,
} from './ColorPickerPopover.utils'
import { ColorPickerPopoverActions } from './ColorPickerPopoverActions'
import { ColorPickerPopoverHueSlider } from './ColorPickerPopoverHueSlider'
import { ColorPickerPopoverPreview } from './ColorPickerPopoverPreview'
import { ColorPickerPopoverSvSquare } from './ColorPickerPopoverSvSquare'

import * as styles from './ColorPickerPopover.module.css'

function ColorPickerPopover({
  activeKey,
  colorFormat = COLOR_FORMAT_HEX,
  isOpen,
  value = EMPTY_OBJECT,
  onCancel,
  onChange,
  onChangeColorFormat,
  onSave,
}) {
  const { t } = useTranslation()

  const normalizedValue = normalizeColorMap(value)
  const keys = Object.keys(normalizedValue)
  const resolvedActiveKey = keys.includes(activeKey) ? activeKey : keys[0]
  const activeItem = normalizedValue[resolvedActiveKey]
  const isMultiColor = keys.length > 1

  const [hsv, setHsv] = useState(() =>
    activeItem ? hexToHsv(activeItem.color) : { h: 0, s: 0, v: 0 }
  )

  // Reset the HSV working copy when the active item or its color changes —
  // during render instead of in an effect.
  const syncKey = activeItem ? `${resolvedActiveKey}:${activeItem.color}` : null
  const [prevSyncKey, setPrevSyncKey] = useState(syncKey)

  if (activeItem && syncKey !== prevSyncKey) {
    setPrevSyncKey(syncKey)
    setHsv(hexToHsv(activeItem.color))
  }

  function emitChange(nextHsv) {
    setHsv(nextHsv)

    if (!activeItem || !resolvedActiveKey) {
      return
    }

    onChange({
      ...normalizedValue,
      [resolvedActiveKey]: {
        ...activeItem,
        color: hsvToHex(nextHsv),
      },
    })
  }

  function handleChangeFormat(selected) {
    onChangeColorFormat?.(selected.value)
  }

  function handleChangeSlider(nextHue) {
    emitChange({
      ...hsv,
      h: nextHue,
    })
  }

  function handleManualColorChange(key, nextHex) {
    const item = normalizedValue[key]

    if (item) {
      const hex = getCommitHex(nextHex)

      if (key === resolvedActiveKey) {
        setHsv(hexToHsv(hex))
      }

      onChange({
        ...normalizedValue,
        [key]: {
          ...item,
          color: hex,
        },
      })
    }
  }

  return (
    <div className={styles.ColorPickerPopover}>
      <ColorPickerPopoverSvSquare
        color={activeItem.color}
        hsv={hsv}
        onChange={({ s, v }) =>
          emitChange({
            ...hsv,
            s,
            v,
          })
        }
      />

      <div className={styles.sliderRow}>
        <ButtonSelect
          className={styles.formatSelectWrapper}
          controlProps={{
            borderless: true,
            className: styles.formatSelect,
            contentProps: {
              className: styles.formatSelectPlaceholder,
            },
            height: NUMBER_SIZE.size24,
            indicatorsProps: {
              ButtonClear: null,
              className: styles.formatSelectIndicator,
            },
            readonly: true,
          }}
          options={COLOR_FORMATS}
          value={{ label: colorFormat, value: colorFormat }}
          onSelectItem={handleChangeFormat}
        />

        <ColorPickerPopoverHueSlider
          hue={hsv.h}
          onChange={handleChangeSlider}
        />
      </div>

      <div className={styles.previewList}>
        {keys.map(key => {
          const item = normalizedValue[key]

          const editable = !isMultiColor || key === resolvedActiveKey

          return (
            <ColorPickerPopoverPreview
              active={isMultiColor && editable}
              color={item.color}
              colorFormat={colorFormat}
              editable={editable}
              info={item.infoTextKey ? t(item.infoTextKey) : resolvedActiveKey}
              isPopoverOpen={isOpen}
              key={key}
              label={item.textKey ? t(item.textKey) : resolvedActiveKey}
              multiColor={isMultiColor}
              onManualColorChange={
                editable
                  ? nextHex => handleManualColorChange(key, nextHex)
                  : undefined
              }
            />
          )
        })}
      </div>
      <ColorPickerPopoverActions onCancel={onCancel} onSave={onSave} />
    </div>
  )
}

export default ColorPickerPopover
